import React, { useState, useRef, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { auditLog } from '../../../services/metalguard';

export default function ImportConfigModal({ onClose, onSuccess }) {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const EXPECTED_KEYS = ['metals', 'scrap_categories', 'risk_thresholds', 'volatility_settings', 'alert_rules'];

  const parseFile = (f) => {
    setParseError(null);
    setParsed(null);
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e?.target?.result);
        const hasAnyKey = EXPECTED_KEYS?.some(k => k in json);
        if (!hasAnyKey) {
          setParseError('El archivo no es válido o está corrupto. Usa un archivo exportado desde MetalRecovery.');
          setFile(null);
          return;
        }
        setParsed(json);
      } catch {
        setParseError('El archivo no es válido o está corrupto. Usa un archivo exportado desde MetalRecovery.');
        setFile(null);
      }
    };
    reader?.readAsText(f);
  };

  const handleFileChange = (e) => {
    const f = e?.target?.files?.[0];
    if (f) parseFile(f);
  };

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setDragOver(false);
    const f = e?.dataTransfer?.files?.[0];
    if (f && f?.name?.endsWith('.json')) parseFile(f);
    else setParseError('Solo se aceptan archivos .json');
  }, []);

  const handleDragOver = (e) => { e?.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const stripId = (obj) => {
    const { id, ...rest } = obj;
    return rest;
  };

  const handleImport = async () => {
    if (!parsed) return;
    setImporting(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const userId = user?.id;
      let totalRestored = 0;

      // Import metals
      if (Array.isArray(parsed?.metals) && parsed?.metals?.length > 0) {
        const rows = parsed?.metals?.map(m => ({ ...stripId(m), user_id: userId }));
        await supabase?.from('metals')?.upsert(rows, { onConflict: 'user_id,name' });
        totalRestored += rows?.length;
      }

      // Import scrap_categories (need metal mapping by name)
      if (Array.isArray(parsed?.scrap_categories) && parsed?.scrap_categories?.length > 0) {
        // Fetch current metals to remap metal_id by name
        const { data: currentMetals } = await supabase?.from('metals')?.select('id, name')?.eq('user_id', userId);
        const metalMap = {};
        (currentMetals || [])?.forEach(m => { metalMap[m.name] = m?.id; });

        const rows = parsed?.scrap_categories?.map(c => {
          const { id, metal_id, metals, ...rest } = c;
          // Try to remap metal_id by metal name from joined data
          const metalName = metals?.name || c?.metal_name;
          const remappedMetalId = metalName ? metalMap?.[metalName] : undefined;
          return { ...rest, user_id: userId, ...(remappedMetalId ? { metal_id: remappedMetalId } : {}) };
        });
        await supabase?.from('scrap_categories')?.upsert(rows, { onConflict: 'user_id,display_name' });
        totalRestored += rows?.length;
      }

      // Import risk_thresholds
      if (parsed?.risk_thresholds) {
        const { id, ...rest } = parsed?.risk_thresholds;
        await supabase?.from('risk_thresholds')?.upsert({ ...rest, user_id: userId }, { onConflict: 'user_id' });
        totalRestored += 1;
      }

      // Import volatility_settings
      if (Array.isArray(parsed?.volatility_settings) && parsed?.volatility_settings?.length > 0) {
        const { data: currentMetals } = await supabase?.from('metals')?.select('id, name')?.eq('user_id', userId);
        const metalMap = {};
        (currentMetals || [])?.forEach(m => { metalMap[m.name] = m?.id; });

        const rows = parsed?.volatility_settings?.map(v => {
          const { id, metal_id, metals, ...rest } = v;
          const metalName = metals?.name || v?.metal_name;
          const remappedMetalId = metalName ? metalMap?.[metalName] : undefined;
          return { ...rest, user_id: userId, ...(remappedMetalId ? { metal_id: remappedMetalId } : {}) };
        });
        await supabase?.from('volatility_settings')?.upsert(rows, { onConflict: 'user_id,metal_id' });
        totalRestored += rows?.length;
      }

      // Import alert_rules
      if (Array.isArray(parsed?.alert_rules) && parsed?.alert_rules?.length > 0) {
        const rows = parsed?.alert_rules?.map(a => ({ ...stripId(a), user_id: userId }));
        await supabase?.from('alert_rules')?.upsert(rows, { onConflict: 'user_id,name' });
        totalRestored += rows?.length;
      }

      // Import preferences
      if (parsed?.preferences) {
        await supabase?.from('user_profiles')?.update({ preferences: parsed?.preferences })?.eq('user_id', userId);
        totalRestored += 1;
      }

      // Audit log
      await auditLog('IMPORT_CONFIG', 'config', null, null, { total_restored: totalRestored, imported_at: new Date()?.toISOString() }, 'settings');

      onSuccess(`Configuración importada correctamente — ${totalRestored} elementos restaurados`);
      onClose();
    } catch (err) {
      setParseError('Error durante la importación. Por favor, inténtalo de nuevo.');
    } finally {
      setImporting(false);
    }
  };

  const previewSummary = parsed ? [
    parsed?.metals?.length ? `${parsed?.metals?.length} metales` : null,
    parsed?.scrap_categories?.length ? `${parsed?.scrap_categories?.length} categorías` : null,
    parsed?.alert_rules?.length ? `${parsed?.alert_rules?.length} reglas de alerta` : null,
    parsed?.volatility_settings?.length ? `${parsed?.volatility_settings?.length} configuraciones de volatilidad` : null,
    parsed?.risk_thresholds ? '1 umbral de riesgo' : null,
    parsed?.preferences ? '1 bloque de preferencias' : null,
  ]?.filter(Boolean) : [];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div className="w-full max-w-lg rounded-xl border border-border overflow-hidden" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-xl)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: 'rgba(99,102,241,0.12)' }}>
              <Icon name="Upload" size={18} color="var(--color-accent)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.0625rem', fontWeight: '700' }}>Importar configuración</h2>
              <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>Restaura tu configuración desde un archivo JSON</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded">
            <Icon name="X" size={18} color="currentColor" strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Drop zone */}
          {!parsed && (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef?.current?.click()}
              className={`relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                dragOver ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-muted/30'
              }`}
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{ background: 'var(--color-muted)' }}>
                <Icon name={dragOver ? 'FolderOpen' : 'FileJson'} size={24} color="var(--color-accent)" strokeWidth={1.5} />
              </div>
              <div className="text-center">
                <p className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}>
                  {dragOver ? 'Suelta el archivo aquí' : 'Arrastra tu archivo JSON aquí'}
                </p>
                <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem', marginTop: '2px' }}>
                  o <span style={{ color: 'var(--color-accent)' }}>haz clic para seleccionar</span> · Solo archivos .json
                </p>
              </div>
              <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleFileChange} />
            </div>
          )}

          {/* Parse error */}
          {parseError && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg border" style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.25)' }}>
              <Icon name="AlertCircle" size={16} color="#ef4444" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
              <p style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', color: '#ef4444' }}>{parseError}</p>
            </div>
          )}

          {/* Preview summary */}
          {parsed && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg border" style={{ background: 'rgba(99,102,241,0.06)', borderColor: 'rgba(99,102,241,0.2)' }}>
                <Icon name="FileCheck" size={16} color="var(--color-accent)" strokeWidth={2} />
                <div>
                  <p className="text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', fontWeight: '600' }}>
                    {file?.name}
                  </p>
                  {parsed?.exported_at && (
                    <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>
                      Exportado el {new Date(parsed.exported_at)?.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {parsed?.user_email ? ` · ${parsed?.user_email}` : ''}
                    </p>
                  )}
                </div>
              </div>

              <div className="px-4 py-3 rounded-lg border border-border" style={{ background: 'var(--color-muted)' }}>
                <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Se importarán:</p>
                <div className="flex flex-wrap gap-2">
                  {previewSummary?.map((item, i) => (
                    <span key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-foreground" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>
                      <Icon name="Check" size={12} color="var(--color-success)" strokeWidth={2.5} />
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 px-4 py-3 rounded-lg border" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.25)' }}>
                <Icon name="AlertTriangle" size={16} color="#f59e0b" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <p style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', color: '#f59e0b' }}>
                  La importación sobreescribirá tu configuración actual. Esta acción no se puede deshacer.
                </p>
              </div>

              <button
                onClick={() => { setParsed(null); setFile(null); setParseError(null); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}
              >
                ← Seleccionar otro archivo
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border" style={{ background: 'var(--color-muted)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-border transition-colors"
            style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleImport}
            disabled={!parsed || importing}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: parsed ? '#dc2626' : 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
          >
            {importing ? (
              <><Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" /> Importando...</>
            ) : (
              <><Icon name="Upload" size={14} color="white" strokeWidth={2.5} /> Confirmar importación</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
